import { invariant } from '@epic-web/invariant'

export type IndexedMapCompoundIndex<
  T extends object,
  RetType extends Map<unknown, unknown> | Set<unknown>,
> = {
  updateIndex: (params: {
    key: string
    item: T
    oldItem: T | null
    currentValue: RetType | null
    operation: 'upsert' | 'delete'
  }) => RetType
  cloneIndex: (currentValue: RetType) => RetType
}

type CompoundIndexConfig<T extends object> = {
  [K: string]: IndexedMapCompoundIndex<T, any>
}

type COWState<T extends object> = {
  baseMap: IndexedMap<T, any>
  overrides: Map<string, T>
  deletedKeys: Set<string>
  isDirty: boolean
  // Store COW-specific onChange handler
  onChange?:
    | ((params: {
        item: T
        oldItem: T | null
        operation: 'upsert' | 'delete'
      }) => void)
    | null
  // Store COW-specific onIndexConflict handler
  onIndexConflict?:
    | ((params: {
        key: string
        indexKey: keyof T
        item: T
        existingItem: T
      }) => { keep: 'first' | 'last'; deleteUnused?: boolean })
    | null
}

export type CreateIndexedMapParams<
  T extends object,
  TIndexes extends CompoundIndexConfig<T>,
> = {
  /**
   * Keys to automatically create indexes for
   */
  indexKeys?: (keyof T)[]
  /**
   * Keys to automatically try when looking up items if the main key fails
   */
  autoFallbackKeys?: (keyof T)[]
  /**
   * Custom compound indexes
   */
  compoundIndexes?: TIndexes
  /**
   * Called whenever an item is added, updated, or deleted
   */
  onChange?: (params: {
    item: T
    oldItem: T | null
    operation: 'upsert' | 'delete'
  }) => void
  /**
   * Called when an index is about to overwrite an existing key/item
   */
  onIndexConflict?: (params: {
    key: string
    indexKey: keyof T
    item: T
    existingItem: T
  }) => { keep: 'first' | 'last'; deleteUnused?: boolean }
  /**
   * Internal: Copy-on-write state for efficient cloning
   */
  _cowState?: COWState<T>
}

// Helper type to extract the return type from a compound index
type CompoundIndexReturnType<T> =
  T extends IndexedMapCompoundIndex<any, infer R> ? R : never

export type IndexedMap<
  T extends object,
  TIndexes extends CompoundIndexConfig<T> = Record<string, never>,
> = {
  mainData: Map<string, T>
  /** Get an item by its primary key */
  get: (
    key: string,
    opts?: { allowLinearSearch?: boolean; fallbackKey?: keyof T | false }
  ) => T | null
  /** Get an item by any indexed property */
  getBy: <K extends keyof T>(
    key: K,
    value: T[K],
    opts?: { allowLinearSearch?: boolean }
  ) => T | null
  getMainKey: (
    key: string,
    opts?: { allowLinearSearch?: boolean; fallbackKey?: keyof T | false }
  ) => string | null
  /** Get an item's primary key by any indexed property */
  getKeyBy: <K extends keyof T>(
    key: K,
    value: T[K],
    opts?: { allowLinearSearch?: boolean }
  ) => string | null
  /** Set or update an item */
  set: (key: string, value: T) => void
  /** Delete an item */
  delete: (key: string) => boolean
  /** Check if an item exists */
  has: (
    key: string,
    opts?: { allowLinearSearch?: boolean; fallbackKey?: keyof T | false }
  ) => boolean
  /** Get all values */
  values: () => IterableIterator<T>
  /** Get all entries */
  entries: () => IterableIterator<[string, T]>
  /** Get all primary keys */
  keys: () => IterableIterator<string>
  /** Get number of items */
  size: () => number
  /** Clear the map */
  clear: () => void
  /** Iterate over all items */
  forEach: (
    callback: (value: T, key: string, map: IndexedMap<T, TIndexes>) => void
  ) => void
  /** Clone the map */
  clone: () => IndexedMap<T, TIndexes>
  /** Create a copy-on-write clone (efficient shallow clone) */
  copyOnWrite: (options?: {
    onChange?:
      | ((params: {
          item: T
          oldItem: T | null
          operation: 'upsert' | 'delete'
        }) => void)
      | null
    onIndexConflict?:
      | ((params: {
          key: string
          indexKey: keyof T
          item: T
          existingItem: T
        }) => { keep: 'first' | 'last'; deleteUnused?: boolean })
      | null
  }) => IndexedMap<T, TIndexes>
  /** Get all indexes */
  indexes: Map<keyof T, Map<T[keyof T], string>>
  /** Get compound indexes */
  compoundIndexes: {
    _root: Map<string, Map<unknown, unknown> | Set<unknown>>
    get: <K extends keyof TIndexes>(
      key: K
    ) => CompoundIndexReturnType<TIndexes[K]> | null
  }
}

export function createIndexedMap<
  T extends object,
  TIndexes extends CompoundIndexConfig<T> = Record<string, never>,
>(params?: CreateIndexedMapParams<T, TIndexes>): IndexedMap<T, TIndexes> {
  const mainData = new Map<string, T>()
  const indexes = new Map<keyof T, Map<T[keyof T], string>>()
  const allIndexKeys = new Set([
    ...(params?.indexKeys ?? []),
    // ensure that autoFallbackKeys are also indexed
    ...(params?.autoFallbackKeys ?? []),
  ])
  const compoundIndexes = new Map<
    string,
    Map<unknown, unknown> | Set<unknown>
  >()
  const autoFallbackKeys = new Set(params?.autoFallbackKeys ?? [])

  // Copy-on-write state
  const cowState = params?._cowState

  // Initialize indexes for all keys including fallbacks
  allIndexKeys.forEach((key) => {
    indexes.set(key, new Map())
  })

  // Helper to ensure COW is triggered and indexes are copied
  function ensureCOWTriggered() {
    if (cowState && !cowState.isDirty) {
      cowState.isDirty = true
      // We don't need to copy indexes immediately - they'll be built lazily
    }
  }

  function updateIndexes(
    key: string,
    item: T,
    oldItem: T | null,
    operation: 'upsert' | 'delete'
  ) {
    let willDeleteThisKey = false

    allIndexKeys.forEach((indexKey) => {
      if (willDeleteThisKey) return

      const indexValue = item[indexKey]
      if (indexValue != null) {
        const indexMap = indexes.get(indexKey) ?? new Map()

        if (operation === 'upsert') {
          const existingItemKey = indexMap.get(indexValue)
          const existingItem = existingItemKey
            ? get(existingItemKey) // Use our get method which handles COW
            : null
          let keep: 'last' | 'first' = 'last'
          let deleteUnused = false
          const onIndexConflict =
            cowState?.onIndexConflict === null
              ? null
              : (cowState?.onIndexConflict ?? params?.onIndexConflict)
          if (
            existingItem &&
            existingItemKey &&
            existingItemKey !== key &&
            onIndexConflict
          ) {
            const resolution = onIndexConflict({
              key,
              indexKey,
              item,
              existingItem,
            })
            keep = resolution.keep
            deleteUnused = resolution.deleteUnused ?? false
          }

          if (
            oldItem &&
            oldItem[indexKey] &&
            oldItem[indexKey] !== indexValue
          ) {
            indexMap.delete(oldItem[indexKey])
          }

          if (keep === 'last') {
            indexMap.set(indexValue, key)
            if (deleteUnused) {
              deleteItem(existingItemKey)
            }
          } else if (keep === 'first') {
            if (deleteUnused) {
              willDeleteThisKey = true
            }
          } else {
            throw new Error(
              `Index conflict for key ${key} and index key ${String(
                indexKey
              )} with value ${indexValue}`
            )
          }
        } else if (operation === 'delete') {
          if (indexMap.get(indexValue) === key) {
            indexMap.delete(indexValue)
          }
        }

        indexes.set(indexKey, indexMap)
      }
    })

    if (willDeleteThisKey) {
      // this will come again through updateIndexes to clear them up
      // so we stop the operation
      deleteItem(key)
      return
    }

    // Update compound indexes
    if (params?.compoundIndexes) {
      Object.entries(params.compoundIndexes).forEach(
        ([indexKey, { updateIndex }]) => {
          const current = compoundIndexes.get(indexKey) ?? null
          const updated = updateIndex({
            key,
            item,
            oldItem,
            currentValue: current,
            operation,
          })
          compoundIndexes.set(indexKey, updated)
        }
      )
    }

    // Call onChange handler (use COW-specific logic if in COW mode)
    if (cowState) {
      const cowOnChange = cowState.onChange
      if (cowOnChange !== null) {
        if (cowOnChange) {
          // Use custom COW onChange handler
          cowOnChange({
            item,
            oldItem,
            operation,
          })
        } else if (cowOnChange === undefined) {
          // No explicit COW onChange set, use original onChange
          params?.onChange?.({
            item,
            oldItem,
            operation,
          })
        }
        // If cowOnChange === null, explicitly disable onChange
      }
    } else {
      // Non-COW mode: use original onChange
      params?.onChange?.({
        item,
        oldItem,
        operation,
      })
    }
  }

  function get(
    key: string,
    opts?: {
      allowLinearSearch?: boolean
      isMainKey?: boolean
      fallbackKey?: keyof T | false
    }
  ): T | null {
    const mainKey = opts?.isMainKey ? key : getMainKey(key, opts)
    if (mainKey) {
      return (
        cowState?.overrides.get(mainKey) ??
        mainData.get(mainKey) ??
        cowState?.baseMap.mainData.get(mainKey) ??
        null
      )
    }
    return null
  }

  function has(
    key: string,
    opts?: { allowLinearSearch?: boolean; fallbackKey?: keyof T | false }
  ): boolean {
    return !!getMainKey(key, opts)
  }

  function getBy<K extends keyof T>(
    key: K,
    value: T[K],
    opts?: { allowLinearSearch?: boolean }
  ): T | null {
    const resolvedKey = getKeyBy(key, value, opts)
    if (resolvedKey) return get(resolvedKey)
    return null
  }

  /**
   * Figure out if the provided key is the main key or a fallback key. Return the main key.
   */
  function getMainKey(
    key: string,
    opts?: { allowLinearSearch?: boolean; fallbackKey?: keyof T | false }
  ): string | null {
    if (cowState) {
      if (cowState.overrides.has(key)) {
        return key
      }
    }

    if (mainData.has(key)) {
      return key
    }

    if (opts?.fallbackKey !== false && autoFallbackKeys.size > 0) {
      for (const fallbackKey of autoFallbackKeys) {
        if (opts?.fallbackKey && opts.fallbackKey !== fallbackKey) continue
        const foundKey = getKeyBy(fallbackKey, key as any, opts)
        if (foundKey) {
          if (cowState && cowState.deletedKeys.has(foundKey)) {
            return null
          }
          return foundKey
        }
      }
    }

    if (cowState?.baseMap.mainData.has(key)) {
      if (cowState.deletedKeys.has(key)) return null
      return key
    }

    return null
  }

  function getKeyBy<K extends keyof T>(
    key: K,
    value: T[K],
    opts?: { allowLinearSearch?: boolean }
  ): string | null {
    if (indexes.has(key)) {
      const indexMap = indexes.get(key)!
      if (indexMap.has(value)) {
        return indexMap.get(value) ?? null
      }
    }

    if (cowState) {
      if (opts?.allowLinearSearch) {
        // Check overrides
        for (const [
          overrideKey,
          overrideItem,
        ] of cowState.overrides.entries()) {
          if (
            !cowState.deletedKeys.has(overrideKey) &&
            overrideItem[key] === value
          ) {
            return overrideKey
          }
        }
      }

      // Check base map, excluding deleted/overridden keys
      const baseKey = cowState.baseMap.getKeyBy(key, value, opts)
      if (
        baseKey &&
        !cowState.deletedKeys.has(baseKey) &&
        !cowState.overrides.has(baseKey)
      ) {
        return baseKey
      }

      return null
    }

    if (opts?.allowLinearSearch) {
      // Fallback to linear search
      for (const [itemKey, item] of mainData.entries()) {
        if (item[key] === value) {
          return itemKey
        }
      }
    }

    return null
  }

  function set(key: string, value: T): void {
    const mainKey = getMainKey(key)
    const oldItem = mainKey ? get(mainKey, { isMainKey: true }) : null
    const actualKey = mainKey ?? key

    if (cowState) {
      ensureCOWTriggered()
      cowState.overrides.set(actualKey, value)
      cowState.deletedKeys.delete(actualKey) // Remove from deleted if it was there
    } else {
      mainData.set(actualKey, value)
    }
    updateIndexes(actualKey, value, oldItem, 'upsert')
  }

  function deleteItem(key: string): boolean {
    const mainKey = getMainKey(key)
    if (!mainKey) return false
    const item = get(mainKey, { isMainKey: true })
    invariant(item, `Item ${mainKey} not found`)
    updateIndexes(mainKey, item, item, 'delete')

    if (cowState) {
      ensureCOWTriggered()
      cowState.deletedKeys.add(mainKey)
      cowState.overrides.delete(mainKey) // Remove from overrides if it was there
    } else {
      mainData.delete(mainKey)
    }
    return true
  }

  function* getAllKeys(): IterableIterator<string> {
    if (cowState) {
      // Yield from overrides first
      for (const key of cowState.overrides.keys()) {
        if (!cowState.deletedKeys.has(key)) {
          yield key
        }
      }

      // Then yield from base map, excluding overridden and deleted keys
      for (const key of cowState.baseMap.keys()) {
        if (!cowState.deletedKeys.has(key) && !cowState.overrides.has(key)) {
          yield key
        }
      }
    } else {
      yield* mainData.keys()
    }
  }

  function* getAllValues(): IterableIterator<T> {
    if (cowState) {
      // Yield from overrides first
      for (const [key, value] of cowState.overrides.entries()) {
        if (!cowState.deletedKeys.has(key)) {
          yield value
        }
      }

      // Then yield from base map, excluding overridden and deleted keys
      for (const key of cowState.baseMap.keys()) {
        if (!cowState.deletedKeys.has(key) && !cowState.overrides.has(key)) {
          const value = cowState.baseMap.get(key)
          if (value) yield value
        }
      }
    } else {
      yield* mainData.values()
    }
  }

  function* getAllEntries(): IterableIterator<[string, T]> {
    if (cowState) {
      // Yield from overrides first
      for (const [key, value] of cowState.overrides.entries()) {
        if (!cowState.deletedKeys.has(key)) {
          yield [key, value]
        }
      }

      // Then yield from base map, excluding overridden and deleted keys
      for (const key of cowState.baseMap.keys()) {
        if (!cowState.deletedKeys.has(key) && !cowState.overrides.has(key)) {
          const value = cowState.baseMap.get(key)
          if (value) yield [key, value]
        }
      }
    } else {
      yield* mainData.entries()
    }
  }

  function getSize(): number {
    if (cowState) {
      let count = 0
      for (const _key of getAllKeys()) {
        count++
      }
      return count
    }
    return mainData.size
  }

  function fullClone(): IndexedMap<T, TIndexes> {
    const clone = createIndexedMap<T, TIndexes>(params)
    for (const key of getAllKeys()) {
      const value = get(key)
      if (value) {
        clone.set(key, value)
      }
    }
    return clone
  }

  const result = {
    mainData,
    get,
    getBy,
    getKeyBy,
    getMainKey,
    set,
    delete: deleteItem,
    has,
    values: getAllValues,
    entries: getAllEntries,
    keys: getAllKeys,
    size: getSize,
    clear: () => {
      if (cowState) {
        ensureCOWTriggered()
        cowState.overrides.clear()
        cowState.deletedKeys.clear()
        // Add all base keys to deleted
        for (const key of cowState.baseMap.keys()) {
          cowState.deletedKeys.add(key)
        }
      } else {
        mainData.clear()
        indexes.clear()
        compoundIndexes.clear()
      }
    },
    forEach: (
      callback: (value: T, key: string, map: IndexedMap<T, TIndexes>) => void
    ) => {
      for (const key of getAllKeys()) {
        const value = get(key)
        if (value) {
          callback(value, key, result)
        }
      }
    },
    clone: fullClone,
    copyOnWrite: (options?: {
      onChange?:
        | ((params: {
            item: T
            oldItem: T | null
            operation: 'upsert' | 'delete'
          }) => void)
        | null
      onIndexConflict?:
        | ((params: {
            key: string
            indexKey: keyof T
            item: T
            existingItem: T
          }) => { keep: 'first' | 'last'; deleteUnused?: boolean })
        | null
    }) => {
      const newCOWState: COWState<T> = {
        baseMap: result as IndexedMap<T, any>,
        overrides: new Map(),
        deletedKeys: new Set(),
        isDirty: false,
        onChange: options?.onChange,
        onIndexConflict: options?.onIndexConflict,
      }

      // For nested COW: if we're already a COW, duplicate stuff
      if (cowState) {
        newCOWState.overrides = new Map(cowState.overrides)
        newCOWState.deletedKeys = new Set(cowState.deletedKeys)
        newCOWState.baseMap = cowState.baseMap
      }

      const result2 = createIndexedMap<T, TIndexes>({
        ...params,
        _cowState: newCOWState,
      })

      // HYDRATE INDEXES: Copy base indexes to CoW instance
      for (const [key, indexMap] of indexes.entries()) {
        result2.indexes.set(key, new Map(indexMap))
      }

      // HYDRATE COMPOUND INDEXES: Copy current CoW compound indexes to nested CoW instance
      for (const [key, compoundIndex] of compoundIndexes.entries()) {
        const indexConfig = params?.compoundIndexes?.[key]
        if (indexConfig) {
          result2.compoundIndexes._root.set(
            key,
            indexConfig.cloneIndex(compoundIndex as any)
          )
        }
      }

      return result2
    },
    indexes,
    compoundIndexes: {
      _root: compoundIndexes,
      get: <K extends keyof TIndexes>(key: K) => {
        const indexKey = key as string
        const indexConfig = params?.compoundIndexes?.[indexKey]
        if (!indexConfig) return null

        return compoundIndexes.get(indexKey) as CompoundIndexReturnType<
          TIndexes[K]
        > | null
      },
    },
  }

  return result
}

export type IndexedMapValue<T extends IndexedMap<any, any>> = ReturnType<
  T['get']
>
